/**
 * Analyses UI Mapper
 *
 * Transforms API types (AnalysisWithDetails) to UI types (AnalysisData).
 * Transcript is loaded separately via GET /records/:id/transcript.
 *
 * Migration Status: SSOT Pipeline (Sprint 2)
 * ==========================================
 * - API types from: @aica/api-types (SSOT via Zod schema inference)
 * - UI types: defined here (UI-specific, not generated)
 * - Mapper: pure function, no side effects, testable
 *
 * DRY: Uses shared transformation functions from @aica/core:
 * - toXMLAnalysisResult() - converts flat PROMPT_BUILDER_SCHEMA to XMLAnalysisResult
 *
 * Usage:
 *   import { toAnalysisData, toTranscriptDataFromApi } from '@/lib/api/mappers/analyses.mapper';
 *   const uiAnalysis = toAnalysisData(apiAnalysis);
 *   const uiTranscript = toTranscriptDataFromApi(apiTranscript);
 */

import type { AnalysisVerificationState, VoiceMetrics } from '@aica/api-types/schemas';
import {
  analysisVerificationStateSchema,
  humanReviewReasonCodeSchema,
  isCoherentNarrativeIssue,
  storedNarrativeIssueSchema,
  voiceMetricsSchema,
} from '@aica/api-types/schemas';
import type { AnalysisWithDetails, TranscriptResponse } from '../types';
import type { XMLAnalysisResult } from '@/components/details/AnalysisPanel';
import type { DiarizationSegment } from '@/components/details/TranscriptViewer';
import i18n from '@/i18n/config';
import {
  toXMLAnalysisResult,
  canonicalizeQualityWeights,
  computeCanonicalQualityScoreWith,
  COMPONENT_METRICS,
  isComponentMetric,
  isCountedSubMetric,
  qualityWeightSharesWith,
  scoredWithEarlierWeights,
  type AnalysisOutputCamelCase,
  type QualitySubMetricKey,
} from '@aica/core';

/**
 * UI Transcript type - optimized for TranscriptViewer component
 */
export interface TranscriptData {
  id: string;
  recordId: string;
  asrVendor?: string;
  lang?: string;
  text: string;
  diarization: DiarizationSegment[];
  costAsr: number;
  status: string;
  createdAt: string;
}

/**
 * UI Analysis type - optimized for AnalysisPanel component
 *
 * All analyses use XMLAnalysisResult format (24-field PROMPT_BUILDER_SCHEMA).
 */
export interface AnalysisData {
  id: string;
  recordId: string;
  transcriptId?: string;
  llmVendor?: string;
  llmModel?: string;
  promptProfileId?: string;
  result: XMLAnalysisResult;
  scores: Record<string, number>;
  /**
   * Interpreted acoustic voice metrics (WS7-T10) for the call, when present.
   * Undefined for chats and for calls whose acoustic analysis failed. Never
   * carries emotion inference (excluded upstream — EU AI Act Art. 5(1)(f)).
   */
  voiceMetrics?: VoiceMetrics;
  /**
   * What the verification passes concluded about this analysis.
   *
   * Undefined for analyses stored before system metadata existed, and for any
   * payload whose shape does not parse — the notice then simply does not
   * render, exactly like `voiceMetrics`. Present with an EMPTY
   * `narrativeIssues` for analyses stored before the verdict was persisted:
   * that means "nothing to list", never "nothing was found".
   */
  verification?: AnalysisVerificationState;
  /**
   * The line "scored with the weights of the time of analysis" is due (#874,
   * spec §8): the project's weights at the time of analysis would score THIS
   * call differently from the current ones. False when they agree, and when the
   * analysis recorded no weights (`qualityWeightsAtAnalysis` absent).
   */
  scoredWithEarlierWeights?: boolean;
  /**
   * The share of each criterion in this call's score, in percent (#874, spec
   * §8), when the project's current weights actually weight it; undefined when
   * the score is the plain mean (equal weights, or equal over what the call was
   * rated on) — the card then shows no shares.
   */
  qualityWeightShares?: Partial<Record<QualitySubMetricKey, number>>;
  costLlm: number;
  status: string;
  createdAt: string;
}

/**
 * fullResult structure from API (camelCase after backend transformation)
 */
interface FullResultStructure {
  structuredOutput?: Record<string, unknown>;
  /** Acoustic voice metrics (WS7-T10) — sibling of structuredOutput in result_json. */
  voiceMetrics?: unknown;
}

/**
 * Verification state out of the persisted analysis, or undefined.
 *
 * Two sources, because the pipeline writes them separately: `analysisMetadata`
 * holds the status and the reason codes, `analysisVerification.facts` holds the
 * objections themselves. Undefined when the metadata is absent (analyses from
 * before it existed) or does not parse.
 */
function parseVerificationState(
  structuredOutput: Record<string, unknown> | undefined
): AnalysisVerificationState | undefined {
  const metadata = structuredOutput?.analysisMetadata as
    Record<string, unknown> | undefined;
  if (!metadata) return undefined;
  const facts = (
    structuredOutput?.analysisVerification as
      { facts?: { narrativeIssues?: unknown } } | undefined
  )?.facts;
  const rawCodes = Array.isArray(metadata.reviewReasonCodes)
    ? metadata.reviewReasonCodes
    : [];
  const rawIssues = Array.isArray(facts?.narrativeIssues) ? facts.narrativeIssues : [];
  // Незнакомый элемент отбрасывается поштучно, а не роняет разбор целиком:
  // оба enum'а закрыты, воркеры и ui-worker деплоятся отдельно, и в окне
  // скоса один новый код причины иначе стирал бы предупреждение с экрана.
  // Статус — единственное, без чего говорить нечего.
  const reviewReasonCodes = rawCodes.filter(
    (code) => humanReviewReasonCodeSchema.safeParse(code).success
  );
  // Изымала ли PII-минимизация доказательства В ЭТОМ анализе. Ставится ровно
  // тем, что обнуляет цитату: `unclassifiedEvidenceCount > 0` в
  // `finalizeAnalysis` даёт `PII_MINIMIZATION_FAILED`. Там, где изъятия не
  // было, пустая цитата у `CONTRADICTED` по-прежнему брак верификатора, и
  // исторические записи чистятся, как и задумано.
  //
  // Почему признак АНАЛИЗА, а не отдельного нарекания. Для записей этого
  // конвейера он и так точен: `checkNarrativeIssues` отвергает
  // `CONTRADICTED`/`ENTITY_MISMATCH` с пустой цитатой дважды — по ответу
  // модели и по тому, что реально ляжет в базу, — поэтому у сохранённого
  // нарекания цитата БЫЛА, и пустой она может стать только от минимизации,
  // которая тем же действием поднимает счётчик. Маркер на уровне нарекания не
  // изменил бы здесь ни одного исхода.
  //
  // Остаётся оценка сверху на ИСТОРИЧЕСКИХ записях: там изъятие какого-то
  // другого доказательства сохранит и нарекание, чья цитата была пуста
  // изначально. Маркер не чинит и этого — он не бэкфиллится, исходная цитата
  // стёрта. Выбор между «показать лишнее» и «спрятать нужное» сделан в пользу
  // показа: спрятать изъятую цитату значит потерять нарекание, требующее
  // проверки, и это ровно тот вред, ради которого послабление и заводилось.
  const evidenceMayBeRedacted = reviewReasonCodes.includes('PII_MINIMIZATION_FAILED');
  const parsed = analysisVerificationStateSchema.safeParse({
    verificationStatus: metadata.verificationStatus,
    reviewReasonCodes,
    // The STORED schema, not the verifier's: a persisted quote is a transcript
    // span and may be longer than the model was allowed to return, and
    // filtering by the shorter contract dropped exactly those from the screen.
    narrativeIssues: rawIssues.flatMap((issue) => {
      const parsed = storedNarrativeIssueSchema.safeParse(issue);
      // Тот же предикат, что и на записи (`checkNarrativeIssues`): иначе экран
      // показывал бы то, что конвейер уже признал браком. Нужен именно здесь,
      // а не только на записи, потому что нарекания с `DIRECT_SUPPORT` уже
      // лежат в базе — без этого фильтра они висели бы на своих записях до
      // следующего анализа, то есть всегда (#773).
      //
      // `evidenceMayBeRedacted`: сюда приходит значение, уже прошедшее
      // PII-минимизацию (`finalizeAnalysis`), а она обнуляет `evidenceQuote`
      // целиком. В анализе, где изъятие было, пустая цитата у `CONTRADICTED`
      // неотличима от изъятой, и требовать её значит молча снимать с экрана
      // законное нарекание. Где изъятия НЕ было — правило остаётся строгим.
      return parsed.success &&
        isCoherentNarrativeIssue(parsed.data, { evidenceMayBeRedacted })
        ? [parsed.data]
        : [];
    }),
  });
  return parsed.success ? parsed.data : undefined;
}

/**
 * Check if structuredOutput is PROMPT_BUILDER_SCHEMA format
 * (has qualityScore or saleProbability fields)
 */
function isPromptBuilderFormat(output: Record<string, unknown> | undefined): boolean {
  if (!output) return false;
  return (
    'qualityScore' in output ||
    'saleProbability' in output ||
    'greetingIntro' in output ||
    'needsQualification' in output
  );
}

/**
 * Demo analyses (@aica/demo-data `synthesizeAnalysis`) store `result_json` as a
 * raw XMLAnalysisResult — mirrored field-for-field — tagged with
 * `meta.analysisVersion: 'demo-synth-1'`. Their `structuredOutput` carries only
 * the sections the dashboards read (#1498), not the full PROMPT_BUILDER
 * payload, so the panel must read the rich demo shape and not that partial
 * output — and not the degraded A-G-only fallback either (which drops the
 * overall score and most sections).
 */
function isDemoAnalysisResult(fullResult: unknown): boolean {
  const meta = (fullResult as { meta?: { analysisVersion?: unknown } } | null | undefined)
    ?.meta;
  return meta?.analysisVersion === 'demo-synth-1';
}

/**
 * Check if metrics contain A-G scores (greetingScore, needsScore, etc.)
 * Uses COMPONENT_METRICS from @aica/core for DRY consistency
 */
function hasAGMetrics(metrics: Record<string, number>): boolean {
  return COMPONENT_METRICS.some((key) => key in metrics);
}

/**
 * Enrich XMLAnalysisResult with A-G metrics from D1
 *
 * When LLM returns new format (qualityScore) but also A-G component scores,
 * the metrics are stored separately in D1. This function merges them into
 * the XMLAnalysisResult dialogue section for proper UI rendering.
 */
function enrichWithAGMetrics(
  result: XMLAnalysisResult,
  metrics: Record<string, number>
): XMLAnalysisResult {
  if (!hasAGMetrics(metrics)) {
    return result;
  }

  // Initialize dialogue if not present
  const dialogue = result.dialogue || {};

  // Map A-G metrics to dialogue structure
  if (metrics.greetingScore !== undefined && !dialogue.greetingIntro) {
    dialogue.greetingIntro = {
      used: metrics.greetingScore > 50,
      score: metrics.greetingScore,
    };
  }

  if (metrics.needsScore !== undefined && !dialogue.needsQualification) {
    dialogue.needsQualification = {
      totalScore: metrics.needsScore,
    };
  }

  if (metrics.objectionScore !== undefined && !dialogue.objectionHandling) {
    dialogue.objectionHandling = {
      averageScore: metrics.objectionScore,
    };
  }

  if (metrics.closingScore !== undefined && !dialogue.closingTechnique) {
    dialogue.closingTechnique = {
      score: metrics.closingScore,
    };
  }

  // Map emotional metrics (politeness, clarity)
  const emotional = result.emotional || {};

  if (metrics.politenessScore !== undefined && !emotional.politeness) {
    emotional.politeness = {
      score: metrics.politenessScore,
    };
  }

  if (metrics.clarityScore !== undefined && !emotional.clarity) {
    emotional.clarity = {
      score: metrics.clarityScore,
    };
  }

  // Map compliance metrics
  const compliance = result.compliance || {};

  if (metrics.crmScore !== undefined && !compliance.crmCompleteness) {
    compliance.crmCompleteness = {
      passed: metrics.crmScore >= 50, // Consider passed if score >= 50
      score: metrics.crmScore,
    };
  }

  return {
    ...result,
    dialogue: Object.keys(dialogue).length > 0 ? dialogue : undefined,
    emotional: Object.keys(emotional).length > 0 ? emotional : undefined,
    compliance: Object.keys(compliance).length > 0 ? compliance : undefined,
  };
}

/**
 * Transform API analysis to UI format
 *
 * DRY: Uses shared toXMLAnalysisResult() from @aica/core for format transformation.
 *
 * @param apiAnalysis - API analysis (AnalysisWithDetails)
 * @returns UI analysis (AnalysisData)
 *
 * @example
 * const apiAnalysis = await apiClient.analyses.getAnalysisById(analysisId);
 * const uiAnalysis = toAnalysisData(apiAnalysis);
 */
export function toAnalysisData(apiAnalysis: AnalysisWithDetails): AnalysisData {
  // Type-safe access to nested fullResult (now camelCase from backend)
  const fullResult = apiAnalysis.fullResult as FullResultStructure | null | undefined;
  const structuredOutput = fullResult?.structuredOutput;

  // Cast metrics to expected type (API returns Record<string, unknown>), minus a
  // sub-metric the score itself skips (`isCountedSubMetric`: beyond
  // QUALITY_SUB_METRIC_LIMIT, #1889). Left in, the card would show the overall
  // score without it beside `1e+305/100` and an inflated progress bar; dropped,
  // the row reads as "no data", like every other reader of the score. Only the
  // six sub-metrics: `crmScore` is limited by the writer itself (#2539), and
  // the stored `qualityScore` is not a sub-metric.
  const metrics = Object.fromEntries(
    Object.entries((apiAnalysis.metrics || {}) as Record<string, number>).filter(
      ([key, value]) =>
        typeof value !== 'number' || !isComponentMetric(key) || isCountedSubMetric(value)
    )
  );

  // Transform to XMLAnalysisResult
  let result: XMLAnalysisResult;

  if (isDemoAnalysisResult(fullResult)) {
    // Demo records store result_json as a raw XMLAnalysisResult (see
    // isDemoAnalysisResult). Checked first: their partial `structuredOutput`
    // has `needsQualification`, which would pass isPromptBuilderFormat and
    // render a panel without most sections. enrichWithAGMetrics() then no-ops
    // (all sections already present) but stays for symmetry with the real path.
    result = fullResult as unknown as XMLAnalysisResult;
  } else if (structuredOutput && isPromptBuilderFormat(structuredOutput)) {
    // PROMPT_BUILDER_SCHEMA format → transform to XMLAnalysisResult using shared function (DRY)
    result = toXMLAnalysisResult(
      structuredOutput as AnalysisOutputCamelCase
    ) as XMLAnalysisResult;
  } else {
    // Intentional fallback for legacy/non-PROMPT_BUILDER_SCHEMA formats.
    //
    // WHY: Before PROMPT_BUILDER_SCHEMA was introduced, LLMs returned free-form
    // JSON with arbitrary shapes. Those old analyses are still in the DB and
    // will never be re-analysed, so this branch exists to render them in a
    // degraded-but-functional way rather than crashing the UI.
    //
    // EXPECTED DATA: structuredOutput may have { summary, strengths } fields
    // from the old prompt templates, or may be null/undefined entirely. The
    // enrichWithAGMetrics() call below will still add any A-G component scores
    // that were stored separately in the metrics column.
    result = {
      textSummary:
        (structuredOutput?.summary as string | undefined) || apiAnalysis.summary || '',
      recommendations: {
        strengths: (structuredOutput?.strengths as string[] | undefined) || [],
        improvements: [],
      },
    };
  }

  // Enrich with component metrics from D1 if available
  result = enrichWithAGMetrics(result, metrics);

  // The card's score IS the list row's score: the canonical mean of the stored
  // metrics (#1203), whichever branch built `result`. Demo analyses carry their
  // own `summary.qualityScore` (a demo fixture's 49 over a sub-metric mean of
  // 50), and opening a record must not change the number its row showed.
  //
  // Scored with the project's CURRENT weights (#874) — the list row, the
  // dashboards and the public API read the same ones. `canonicalizeQualityWeights`
  // is what every reader applies: `null` and an all-equal object are one "equal".
  // The score helpers below read the RAW `apiAnalysis.metrics` on purpose: each
  // applies `isCountedSubMetric` itself, and `scoredWithEarlierWeights` compares
  // the stored `qualityScore`, which the filtered `metrics` above no longer holds
  // for every key.
  const currentWeights = canonicalizeQualityWeights(apiAnalysis.qualityWeights);
  const qualityScore =
    computeCanonicalQualityScoreWith(apiAnalysis.metrics, currentWeights) ?? undefined;
  if (result.summary || qualityScore !== undefined) {
    result = { ...result, summary: { ...result.summary, qualityScore } };
  }

  // Acoustic voice metrics (WS7-T10) — sibling of structuredOutput in
  // result_json. Parsed in isolation with safeParse so a shape drift only drops
  // the Voice Metrics block, never the whole analysis. Absent for chats / failed
  // acoustic runs.
  const voiceMetricsParse = voiceMetricsSchema.safeParse(fullResult?.voiceMetrics);
  const voiceMetrics = voiceMetricsParse.success ? voiceMetricsParse.data : undefined;

  // Verification state, assembled from the two places the pipeline writes it:
  // the system metadata (status + reason codes) and the verification evidence
  // (what the fact pass objected to). Parsed with safeParse for the same reason
  // as voiceMetrics — a shape drift must drop the notice, never the analysis.
  //
  // `narrativeIssues` defaults to []: analyses finalized before the verdict was
  // persisted carry only a count, and the notice then shows status and reasons
  // with nothing to expand.
  const verification = parseVerificationState(structuredOutput);

  return {
    id: apiAnalysis.id,
    recordId: apiAnalysis.recordId,
    llmVendor: apiAnalysis.provider,
    llmModel: apiAnalysis.model,
    promptProfileId: apiAnalysis.promptProfileId ?? undefined,
    scores: metrics,
    result,
    voiceMetrics,
    verification,
    scoredWithEarlierWeights: scoredWithEarlierWeights({
      metrics: apiAnalysis.metrics,
      current: currentWeights,
      atAnalysis: apiAnalysis.qualityWeightsAtAnalysis,
      cardScore: qualityScore ?? null,
    }),
    qualityWeightShares:
      qualityWeightSharesWith(apiAnalysis.metrics, currentWeights) ?? undefined,
    costLlm: (apiAnalysis.costCents || 0) / 100, // cents → dollars
    status: 'completed',
    createdAt: apiAnalysis.createdAt || new Date().toISOString(),
  };
}

/**
 * Transform API transcript response (from /records/:id/transcript) to UI format
 *
 * Used when loading transcript independently of analysis (e.g., when analysis not yet done)
 *
 * @param apiTranscript - Direct API response from GET /records/:id/transcript
 * @returns UI transcript (TranscriptData)
 *
 * @example
 * const apiTranscript = await apiClient.records.getRecordTranscript(recordId);
 * const uiTranscript = toTranscriptDataFromApi(apiTranscript);
 */
export function toTranscriptDataFromApi(
  apiTranscript: TranscriptResponse
): TranscriptData {
  const t = i18n.t.bind(i18n);
  // Map diarization to required shape (speaker/text are required in UI)
  const diarization: DiarizationSegment[] = (apiTranscript.diarization || []).map(
    (seg) => ({
      speaker: seg.speaker || t('records.unknown'),
      text: seg.text || '',
      start: seg.start || 0,
      end: seg.end || 0,
      ...(typeof seg.role === 'string' ? { role: seg.role } : {}),
    })
  );

  return {
    id: apiTranscript.id,
    recordId: apiTranscript.recordId,
    text: apiTranscript.text || '',
    lang: apiTranscript.language || undefined,
    asrVendor: apiTranscript.provider || undefined,
    diarization,
    createdAt: apiTranscript.createdAt || '',
    costAsr: 0, // Not returned by API
    status: 'completed',
  };
}
